import { type FC, useCallback, useEffect, useState } from 'react'

import { MicOn } from '@liveHelper/assets/MicOn'
import {
  contentSection,
  menuCoverImage,
} from '@liveHelper/components/Idle/Idle.twind'
import { requestMicrophoneAccessCTAContainerV2 } from '@liveHelper/components/RequestMicrophoneAccess/RequestMicrophoneAccess.twind'
import {
  expandedHeaderDescription,
  expandedHeaderImageContainer,
  expandedHeaderTitle,
} from '@liveHelper/components/shared/shared.twind'
import {
  CTACloseButton,
  FireworkLogoWrapper,
  IdleExpandedContainer,
} from '@liveHelper/components/shared/styles'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import {
  actionsSelector,
  microphoneMutedSelector,
  paramsSelector,
  useStore,
  visibilityRulesMatchSelector,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import { LIVE_HELPER_WIDGET_BASE_CONTAINER } from '@liveHelper/testIds'
import { customLiveHelperEvent } from '@liveHelper/tracking/customLiveHelperEvent'
import { EventType } from '@liveHelper/tracking/eventType'
import {
  useTrackClickEnableMicrophone,
  useTrackClickStartCall,
  useTrackEnableMicrophoneImpression,
} from '@liveHelper/tracking/useLiveHelperTracking'
import { useGetUtmContent } from '@liveHelper/utils/useGetUtmContent'
import { Close } from '@oneToOne/shared/assets/Close'
import { FireworkBranding } from '@oneToOne/shared/components/FireworkBranding'

import { Button } from '@core/components/twind/Button'
import { useCheckMicrophonePermission } from '@core/devicePermissions/useCheckMicrophonePermission'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'
import { isMobileWeb } from '@core/userAgent'

/**
 * A screen asking users for their request intent before video calls
 * Version 2
 */
export const RequestMicrophoneAccess: FC = () => {
  const { tw } = useTwindContext()
  const { t } = useContextTranslation()

  const chatChannelConfig = useChatChannelConfig()
  const utmContent = useGetUtmContent()

  const microphoneMuted = useStore(microphoneMutedSelector)
  const { microphoneMutedStateToggled } = useStore(actionsSelector)
  const { callAgent } = useStore(visitorPrivateActionsSelector)
  const params = useStore(paramsSelector)
  const visibilityRulesMatch = useStore(visibilityRulesMatchSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)

  const trackEnableMicrophoneImpression = useTrackEnableMicrophoneImpression()
  const trackClickEnableMicrophone = useTrackClickEnableMicrophone()
  const trackClickStartCall = useTrackClickStartCall()

  useEffect(() => {
    trackEnableMicrophoneImpression()
  }, [trackEnableMicrophoneImpression])

  const initiateCall = useCallback(() => {
    if (!callAgent || !visibilityRulesMatch) {
      // Return to idle so the user sees the current availability state
      // (e.g. unavailable) instead of being stuck in request/dialing flow.
      sendUIStateTransitionEvent('MINIMIZE')
      sendUIStateTransitionEvent('EXPAND')
      return
    }

    callAgent?.()
    trackClickStartCall()
    customLiveHelperEvent({
      type: EventType.FW_LIVE_HELPER_CLICK_CALL_AGENT,
      data: {
        widget_id: params?.widget_id,
      },
    })
  }, [
    callAgent,
    params?.widget_id,
    sendUIStateTransitionEvent,
    trackClickStartCall,
    visibilityRulesMatch,
  ])

  const [prompted, setPrompted] = useState(false)

  useEffect(() => {
    if (prompted) {
      initiateCall()
    }
  }, [initiateCall, prompted])

  const { initiateMicrophoneAccess } = useCheckMicrophonePermission({
    onAllowed: () => {
      setPrompted(true)
      if (microphoneMuted) {
        microphoneMutedStateToggled()
      }
    },
    onRejected: () => {
      setPrompted(true)
    },
    onDismissed: () => {
      setPrompted(true)
    },
  })

  const onClickAskMeLater = useCallback(() => {
    trackClickEnableMicrophone({
      enableNow: false,
    })
    initiateCall()
  }, [initiateCall, trackClickEnableMicrophone])

  return (
    <IdleExpandedContainer
      data-testid={LIVE_HELPER_WIDGET_BASE_CONTAINER}
      isMobileWebEnabled={isMobileWeb()}
    >
      <CTACloseButton
        shape="circle"
        aria-label={t(`oneToOne:Minimize Live Helper Dialog`)}
        background="var(--gray-9)"
        buttonSize="sm"
        onClick={(e) => {
          e.stopPropagation()
          sendUIStateTransitionEvent('MINIMIZE')
        }}
        icon={<Close width="16px" fill="var(--white)" label="close" />}
      />
      <div className={tw?.(expandedHeaderImageContainer, 'overflow-hidden')}>
        <div
          className={tw?.(
            menuCoverImage(
              chatChannelConfig?.widget_style?.menu_cover_image_url,
            ),
          )}
        ></div>
      </div>
      <div className={tw?.(contentSection, 'px-10', 'pt-10', 'pb-7')}>
        <div
          className={tw?.(
            'flex',
            'flex-col',
            'items-center',
            'gap-2.5',
            'mb-10',
          )}
        >
          <div
            className={tw?.(expandedHeaderTitle, 'text-lg', 'font-semibold')}
          >
            {t(`oneToOne:We would like to speak to you`)}
          </div>
          <div
            className={tw?.(
              expandedHeaderDescription,
              'text-sm',
              'text-center',
            )}
          >
            <span>
              {t(
                'oneToOne:Enable your microphone to ensure the best possible experience while we assist you.',
              )}
            </span>
          </div>
        </div>
        <div
          className={tw?.(
            requestMicrophoneAccessCTAContainerV2,
            'flex',
            'flex-col',
            'gap-2',
            'my-5',
            'mx-4',
          )}
        >
          <Button
            block
            variant="outlined"
            icon={<MicOn width="16px" height="16px" label="mic on" />}
            onClick={() => {
              trackClickEnableMicrophone({
                enableNow: true,
              })
              initiateMicrophoneAccess()
            }}
          >
            &nbsp;{t('oneToOne:Enable Microphone')}
          </Button>
          <Button block variant="outlined" onClick={onClickAskMeLater}>
            {t('oneToOne:Ask Me Later')}
          </Button>
        </div>
      </div>
      {chatChannelConfig?.widget_style?.firework_branding ? (
        <FireworkLogoWrapper>
          <FireworkBranding
            dark={true}
            utmContent={utmContent}
            lang={params.lang}
          />
        </FireworkLogoWrapper>
      ) : null}
    </IdleExpandedContainer>
  )
}
